import React, { useState, useEffect, useContext } from 'react';
import Select from 'react-select';
import axios from 'axios';
import AuthContext from '../../../context/AuthContext';
import localStyles from './ProductTypeSelectionStep.module.css';
import { extractFilteredEnums } from '../../SingleListingGenerator/utils';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/single-gen';

const MAX_PRODUCT_TYPES = 5;

const ProductTypeSelectionStep = ({ onContinue }) => {
  const { token } = useContext(AuthContext);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchingProductTypes, setIsSearchingProductTypes] = useState(false);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [isFetchingDefinitions, setIsFetchingDefinitions] = useState(false);
  const [fetchedEnums, setFetchedEnums] = useState([]);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState(null);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setProductTypeOptions([]);
      setIsSearchingProductTypes(false);
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      const fetchProductTypes = async () => {
        try {
          setIsSearchingProductTypes(true);
          const response = await axios.get(`${API_URL}/product-types/search`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            params: { keywords: searchQuery.trim() },
            signal: controller.signal,
          });
          const data = response.data || {};
          if (!data.success || !data.data) {
            setProductTypeOptions([]);
            setIsSearchingProductTypes(false);
            return;
          }
          const productTypes =
            data.data.productTypes || data.data.product_types || data.data || [];
          const options = (productTypes || [])
            .map((pt) => {
              const name = pt.name || pt.productType || pt.product_type || pt.productTypeName || pt.product_type_name;
              if (!name) return null;
              return { value: name, label: name };
            })
            .filter(Boolean);
          setProductTypeOptions(options);
        } catch (e) {
          if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') setProductTypeOptions([]);
        }
        setIsSearchingProductTypes(false);
      };
      fetchProductTypes();
    }, 300);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
      setIsSearchingProductTypes(false);
    };
  }, [searchQuery, token]);

  const handleSelectProductType = (option) => {
    if (!option || selectedProductTypes.length >= MAX_PRODUCT_TYPES) return;
    const value = option.value;
    if (selectedProductTypes.some((p) => p.value === value)) return;
    setSelectedProductTypes((prev) => [...prev, { value, label: option.label }]);
    setSearchQuery('');
    setProductTypeOptions([]);
  };

  const removeProductType = (value) => {
    setSelectedProductTypes((prev) => prev.filter((p) => p.value !== value));
  };

  const handleSearchDefinitions = async () => {
    if (selectedProductTypes.length === 0) return;
    setIsFetchingDefinitions(true);
    setFetchedEnums([]);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const results = [];
    for (const pt of selectedProductTypes) {
      try {
        const res = await axios.get(
          `${API_URL}/product-types/${encodeURIComponent(pt.value)}/definition`,
          { headers }
        );
        if (res.data?.success && res.data?.data) {
          const schema = res.data.data;
          const filtered = extractFilteredEnums(schema);
          if (filtered) {
            results.push({ productType: pt.label, enum: filtered.enum });
          }
        }
      } catch (e) {
        // ignore per-type errors
      }
    }
    setFetchedEnums(results);
    setIsFetchingDefinitions(false);
  };

  const handleDownloadTemplate = async () => {
    if (fetchedEnums.length === 0) return;
    setIsDownloadingTemplate(true);
    setTemplateError(null);
    try {
      const payload = {
        product_types: fetchedEnums.map((item) => ({
          name: item.productType,
          themes: item.enum,
        })),
      };
      const response = await axios.post(
        `${API_URL}/product-types/generate-template`,
        payload,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_new_listing_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setTemplateError('Failed to generate template. Please try again.');
    }
    setIsDownloadingTemplate(false);
  };

  const canAddMore = selectedProductTypes.length < MAX_PRODUCT_TYPES;

  return (
    <div className={localStyles.container}>
      <div className={localStyles.pageHeader}>
        <h2 className={localStyles.pageTitle}>Select Product Types</h2>
        <p className={localStyles.subtitle}>
          Search and add up to {MAX_PRODUCT_TYPES} product types. Then fetch their definitions.
        </p>
      </div>

      <div className={localStyles.card}>
        <div className={localStyles.fieldSection}>
          <label className={localStyles.label}>Product Type</label>
          <div className={localStyles.selectWrapper}>
            <Select
              inputId="bulk-product-type-search"
              options={productTypeOptions}
              onChange={handleSelectProductType}
              value={null}
              isSearchable
              isDisabled={!canAddMore}
              placeholder={canAddMore ? 'Type to search product types (min 3 characters)...' : `Maximum ${MAX_PRODUCT_TYPES} product types`}
              classNamePrefix="react-select"
              onInputChange={(v) => setSearchQuery(v)}
              inputValue={searchQuery}
              filterOption={() => true}
              noOptionsMessage={() => {
                if (!searchQuery || searchQuery.trim().length < 3) return 'Type at least 3 characters to search.';
                if (isSearchingProductTypes) return 'Searching...';
                return 'No product types found. Try a different search term.';
              }}
              styles={{
                control: (p) => ({ ...p, border: '1px solid #d0e7ff', borderRadius: 8, minHeight: 42 }),
                menu: (p) => ({ ...p, zIndex: 1000 }),
              }}
            />
          </div>
        </div>

        <div className={localStyles.selectedSection}>
          <label className={localStyles.label}>Selected ({selectedProductTypes.length}/{MAX_PRODUCT_TYPES})</label>
          <div className={localStyles.chipContainer}>
            {selectedProductTypes.length === 0 ? (
              <span className={localStyles.emptyHint}>No product types added yet</span>
            ) : (
              selectedProductTypes.map((pt) => (
                <span key={pt.value} className={localStyles.chip}>
                  {pt.label}
                  <button
                    type="button"
                    className={localStyles.chipRemove}
                    onClick={() => removeProductType(pt.value)}
                    aria-label={`Remove ${pt.label}`}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
        </div>

        <div className={localStyles.actions}>
          <button
            type="button"
            className={localStyles.searchBtn}
            onClick={handleSearchDefinitions}
            disabled={selectedProductTypes.length === 0 || isFetchingDefinitions}
          >
            {isFetchingDefinitions ? 'Fetching...' : 'Fetch definitions'}
          </button>
          <button
            type="button"
            className={localStyles.downloadBtn}
            onClick={handleDownloadTemplate}
            disabled={fetchedEnums.length === 0 || isDownloadingTemplate}
          >
            {isDownloadingTemplate ? 'Generating...' : 'Download Template'}
          </button>
          <button
            type="button"
            className={localStyles.continueBtn}
            onClick={onContinue}
          >
            Continue to upload
          </button>
        </div>
        {templateError && (
          <p className={localStyles.templateError}>{templateError}</p>
        )}
      </div>

      {fetchedEnums.length > 0 && (
        <div className={localStyles.resultsSection}>
          <h3 className={localStyles.resultsTitle}>Filtered enums</h3>
          {fetchedEnums.map((item) => (
            <div key={item.productType} className={localStyles.resultCard}>
              <h4 className={localStyles.resultProductType}>{item.productType}</h4>
              <pre className={localStyles.resultPre}>
                {JSON.stringify(item.enum, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductTypeSelectionStep;
